import { LitElement, html, css, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "../components/index.js";
import "./log-view.js";
import { canView } from "./artifact-viewer.js";
import { toast, buildStatusVariant, type BnColumn, type BnRow } from "../components/index.js";
import { navigate } from "../nav.js";
import { humanError } from "../api/errors.js";
import { subscribeLive } from "../api/realtime.js";
import {
  getBuild,
  getLog,
  listArtifacts,
  getArtifactBlob,
  isTerminal,
  type Build,
  type Artifact,
} from "../api/builds.js";

const POLL_MS = 3000;

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatTime(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

/** short cuts a commit or digest to a readable prefix; the title keeps it whole. */
function short(v: string): string {
  return v.replace(/^sha256:/, "").slice(0, 12);
}

/**
 * hasProvenance reports whether the build recorded its provenance: only a
 * queued build does, and it always has a trigger and a commit.
 */
function hasProvenance(b: Build): boolean {
  return Boolean(b.trigger || b.commit);
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * page-build (/builds/:id) is the build-detail page linked from a workspace's
 * build history. It shows the build's status + timestamps, the captured log, and
 * the produced artifacts with download links. While the build is non-terminal it
 * subscribes to realtime status events (falling back to polling if the stream
 * cannot be established) and refreshes the log/artifacts once it finishes.
 */
@customElement("page-build")
export class PageBuild extends LitElement {
  @property() buildId = "";

  @state() private build: Build | null = null;
  @state() private log = "";
  @state() private artifacts: Artifact[] = [];
  @state() private loading = true;
  @state() private error = "";
  /** The path of the artifact open in the viewer; empty when it is closed. */
  @state() private viewing = "";

  private pollTimer?: number;
  private stopLive?: () => void;
  private liveUp = false;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .back {
      display: inline-flex;
      align-items: center;
      gap: var(--bino-space-xs);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      text-decoration: none;
      margin-bottom: var(--bino-space-md);
    }
    .back:hover {
      color: var(--bino-text);
    }
    .head {
      display: flex;
      align-items: center;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-lg);
      flex-wrap: wrap;
    }
    h1 {
      margin: 0;
      font-size: var(--bino-font-size-lg);
      font-weight: 600;
      color: var(--bino-text);
    }
    .spacer {
      flex: 1;
    }
    .sections {
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-lg);
    }
    .meta {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
      gap: var(--bino-space-md);
    }
    .meta .k {
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-sm);
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--bino-text-secondary);
      margin-bottom: var(--bino-space-xs);
    }
    .meta .v {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text);
      word-break: break-all;
    }
    .err {
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-error);
      margin: 0;
    }
    log-view {
      max-height: 30rem;
    }
    .hint {
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      margin: 0;
    }
    .link-btn {
      background: none;
      border: none;
      padding: 0;
      cursor: pointer;
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-base);
      color: var(--bino-primary);
      text-decoration: underline;
    }
    .link-btn:hover {
      color: var(--bino-primary-hover);
    }
    .v a {
      color: var(--bino-primary);
    }
    h3 {
      margin: var(--bino-space-lg) 0 var(--bino-space-sm);
      font-size: var(--bino-font-size-md);
      font-weight: 600;
    }
    ul.plain {
      margin: 0;
      padding-left: var(--bino-space-lg);
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-md);
    }
  `;

  override willUpdate(changed: PropertyValues) {
    // The router reuses this element for the next build.
    if (changed.has("buildId")) this.viewing = "";
  }

  override updated(changed: PropertyValues) {
    if (changed.has("buildId") && this.buildId) void this.load();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.stopPoll();
    this.stopSubscription();
  }

  /** Subscribes to the build record's realtime events; polls only as fallback. */
  private startSubscription() {
    this.stopSubscription();
    this.stopLive = subscribeLive({
      collection: "builds",
      topic: this.buildId,
      fields: "id,status",
      onEvent: () => void this.poll(),
      // The one-shot poll closes the race where the build went terminal
      // between the initial fetch and the subscription being established.
      onUp: () => {
        this.liveUp = true;
        this.stopPoll();
        void this.poll();
      },
      onDown: () => this.schedulePoll(),
    });
  }

  private stopSubscription() {
    this.stopLive?.();
    this.stopLive = undefined;
    this.liveUp = false;
  }

  private async load() {
    this.loading = true;
    this.error = "";
    try {
      const build = await getBuild(this.buildId);
      this.build = build;
      const [log, artifacts] = await Promise.all([
        getLog(this.buildId).catch(() => ""),
        listArtifacts(this.buildId).catch((): Artifact[] => []),
      ]);
      this.log = log;
      this.artifacts = artifacts;
      if (isTerminal(build.status)) {
        this.stopSubscription();
        this.stopPoll();
      } else {
        this.startSubscription();
      }
    } catch (e) {
      this.error = humanError(e);
    } finally {
      this.loading = false;
    }
  }

  private schedulePoll() {
    if (this.liveUp) return;
    if (this.build && !isTerminal(this.build.status)) {
      if (this.pollTimer === undefined) {
        this.pollTimer = window.setInterval(() => void this.poll(), POLL_MS);
      }
    } else {
      this.stopPoll();
    }
  }

  private stopPoll() {
    if (this.pollTimer !== undefined) {
      window.clearInterval(this.pollTimer);
      this.pollTimer = undefined;
    }
  }

  private async poll() {
    try {
      const build = await getBuild(this.buildId);
      this.build = build;
      if (isTerminal(build.status)) {
        const [log, artifacts] = await Promise.all([
          getLog(this.buildId).catch(() => ""),
          listArtifacts(this.buildId).catch((): Artifact[] => []),
        ]);
        this.log = log;
        this.artifacts = artifacts;
        this.stopSubscription();
      }
      this.schedulePoll();
    } catch {
      this.stopPoll();
    }
  }

  private async downloadArtifact(path: string) {
    try {
      const blob = await getArtifactBlob(this.buildId, path);
      downloadBlob(blob, path.split("/").pop() ?? path);
    } catch (e) {
      toast(humanError(e), "error");
    }
  }

  private buildLink(id: string) {
    const href = `/builds/${encodeURIComponent(id)}`;
    return html`<a
      href=${href}
      @click=${(e: Event) => {
        e.preventDefault();
        navigate(href);
      }}
      >${id}</a
    >`;
  }

  /**
   * renderProvenance says what the build ran on: the commit, the image, the
   * sealed data, bino.lock and the inputs it was started with. Secrets show
   * name, scope and version only, and request env only that there was some:
   * the server keeps no values of either.
   */
  private renderProvenance(b: Build) {
    const params = Object.entries(b.params ?? {});
    const secrets = b.secrets ?? [];
    const undigested = b.undigestedSources ?? [];
    const digest = (v: string | undefined) =>
      v ? html`<span title=${v}>${short(v)}</span>` : "—";
    return html`<bn-card heading="Provenance">
      <div class="meta">
        <div><div class="k">Commit</div><div class="v">${digest(b.commit)}</div></div>
        <div><div class="k">Trigger</div><div class="v">${b.trigger || "—"}</div></div>
        <div><div class="k">Started by</div><div class="v">${b.actorId || "—"}</div></div>
        <div>
          <div class="k">Image</div>
          <div class="v">
            ${b.image || "—"}
            ${b.imageDigest ? html`<br />${digest(b.imageDigest)}` : nothing}
          </div>
        </div>
        <div><div class="k">bino.lock</div><div class="v">${digest(b.lockDigest)}</div></div>
        <div>
          <div class="k">Sealed data</div>
          <div class="v">${b.sealSetDigest ? digest(b.sealSetDigest) : "none"}</div>
        </div>
        <div><div class="k">Report period</div><div class="v">${b.reportPeriod || "—"}</div></div>
        <div>
          <div class="k">Artefacts</div>
          <div class="v">${b.artefacts?.length ? b.artefacts.join(", ") : "all"}</div>
        </div>
        <div>
          <div class="k">Request env</div>
          <div class="v">${b.requestEnv ? "yes, not stored" : "none"}</div>
        </div>
      </div>

      ${undigested.length
        ? html`<h3>Sources outside the sealed data</h3>
            <p class="hint">
              The build read these sources directly, so a rerun of the same commit and seals can
              give a different result.
            </p>
            <ul class="plain">
              ${undigested.map((u) => html`<li>${u.kind} ${u.name}</li>`)}
            </ul>`
        : nothing}
      ${params.length
        ? html`<h3>Parameters</h3>
            <bn-table
              .columns=${[
                { key: "name", label: "Name", mono: true },
                { key: "value", label: "Value", mono: true },
              ] as BnColumn[]}
              .rows=${params.map(([name, value]): BnRow => ({ name, value }))}
            ></bn-table>`
        : nothing}
      ${secrets.length
        ? html`<h3>Secrets</h3>
            <bn-table
              .columns=${[
                { key: "name", label: "Name", mono: true },
                { key: "scope", label: "Scope", width: "1px" },
                { key: "version", label: "Version", align: "right", mono: true, width: "1px" },
              ] as BnColumn[]}
              .rows=${secrets.map(
                (x): BnRow => ({ name: x.name, scope: x.scope, version: String(x.version) }),
              )}
            ></bn-table>`
        : nothing}
    </bn-card>`;
  }

  private duration(b: Build): string {
    if (!b.started || !b.finished) return "—";
    const ms = new Date(b.finished).getTime() - new Date(b.started).getTime();
    if (isNaN(ms)) return "—";
    return `${Math.max(0, Math.round(ms / 1000))}s`;
  }

  override render() {
    if (this.loading && !this.build) {
      return html`<bn-spinner block label="Loading build…"></bn-spinner>`;
    }
    if (this.error || !this.build) {
      return html`<bn-empty-state
        icon="⚠"
        heading="Couldn’t load build"
        message=${this.error || "Build not found."}
      >
        <bn-button slot="actions" variant="secondary" @click=${() => navigate("/workspaces")}
          >Back to workspaces</bn-button
        >
      </bn-empty-state>`;
    }

    const b = this.build;
    const live = !isTerminal(b.status);
    const backHref = `/workspaces/${encodeURIComponent(b.workspaceId)}`;
    return html`
      <a
        class="back"
        href=${backHref}
        @click=${(e: Event) => {
          e.preventDefault();
          navigate(backHref);
        }}
        >← Workspace</a
      >
      <div class="head">
        <h1>Build</h1>
        <bn-badge .variant=${buildStatusVariant(b.status)} ?dot=${live}>${b.status}</bn-badge>
        <span class="spacer"></span>
        <bn-button variant="ghost" size="sm" @click=${() => this.load()}>Refresh</bn-button>
      </div>

      <div class="sections">
        <bn-card heading="Details">
          ${b.error
            ? html`<p class="err">${b.error}</p>`
            : nothing}
          ${b.status === "queued"
            ? html`<p class="hint">
                Queued. The build starts when its checkout is ready and a runner is free.
              </p>`
            : nothing}
          <div
            class="meta"
            style=${b.error || b.status === "queued" ? "margin-top:var(--bino-space-md)" : ""}
          >
            <div><div class="k">Build ID</div><div class="v">${b.id}</div></div>
            <div><div class="k">Created</div><div class="v">${formatTime(b.created)}</div></div>
            <div><div class="k">Started</div><div class="v">${formatTime(b.started)}</div></div>
            <div><div class="k">Finished</div><div class="v">${formatTime(b.finished)}</div></div>
            <div><div class="k">Duration</div><div class="v">${this.duration(b)}</div></div>
            ${b.retryOf
              ? html`<div>
                  <div class="k">Retry of</div>
                  <div class="v">${this.buildLink(b.retryOf)}</div>
                </div>`
              : nothing}
            ${b.retriedAs
              ? html`<div>
                  <div class="k">Retried as</div>
                  <div class="v">${this.buildLink(b.retriedAs)}</div>
                </div>`
              : nothing}
          </div>
        </bn-card>

        ${hasProvenance(b) ? this.renderProvenance(b) : nothing}

        <bn-card heading="Log">
          ${this.log
            ? html`<log-view .text=${this.log}></log-view>`
            : html`<p class="hint">${live ? "Waiting for log output…" : "No log output."}</p>`}
        </bn-card>

        <bn-card heading="Files" flush>
          ${this.artifacts.length === 0
            ? html`<div style="padding:var(--bino-space-lg)">
                <p class="hint">No files.</p>
              </div>`
            : html`<bn-table
                .columns=${[
                  { key: "path", label: "Path", mono: true },
                  { key: "size", label: "Size", align: "right", mono: true },
                  { key: "actions", label: "", align: "right", width: "1px" },
                ] as BnColumn[]}
                .rows=${this.artifacts.map(
                  (a): BnRow => ({
                    path: a.path,
                    size: formatBytes(a.size),
                    actions: html`<span style="white-space:nowrap">
                      ${canView(a.path)
                        ? html`<button class="link-btn" @click=${() => (this.viewing = a.path)}>
                            View
                          </button>`
                        : nothing}
                      <button class="link-btn" @click=${() => this.downloadArtifact(a.path)}>
                        Download
                      </button>
                    </span>`,
                  }),
                )}
              ></bn-table>`}
        </bn-card>
      </div>

      <artifact-viewer
        .buildId=${this.buildId}
        .path=${this.viewing}
        .size=${this.artifacts.find((a) => a.path === this.viewing)?.size ?? 0}
        @close=${() => (this.viewing = "")}
        @download=${() => this.downloadArtifact(this.viewing)}
      ></artifact-viewer>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-build": PageBuild;
  }
}
